import { apiService } from './api';
import { logger } from '../utils/logger';
import { validateFile as validateFileType } from '../utils/fileValidation';

import type {
  LessonPlan,
  LessonPlanListParams,
  LessonPlanListResponse,
  LessonPlanFormData,
  LessonPlanRevisionFormData
} from '../types';

export class LessonPlanService {
  private baseUrl = '/api/lesson-plans';

  async getLessonPlans(params: LessonPlanListParams = {}): Promise<LessonPlanListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params.searchTerm !== undefined && params.searchTerm !== null && params.searchTerm.trim() !== '') {
      searchParams.append('search', params.searchTerm.trim());
    }
    if (params.status) searchParams.append('status', params.status);
    if (params.excludeStatus) searchParams.append('excludeStatus', params.excludeStatus);
    if (params.myPlans) searchParams.append('myPlans', params.myPlans.toString());
    if (params.submittedByUserId) searchParams.append('submittedByUserId', params.submittedByUserId);
    if (params.folderId) searchParams.append('folderId', params.folderId);
    if (params.subjectId) searchParams.append('subjectId', params.subjectId);
    if (params.week !== undefined && params.week !== null) searchParams.append('week', params.week.toString());
    if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.append('dateTo', params.dateTo);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const data = await apiService.get<any>(endpoint);

    // Backend sends pagination info in response body
    return {
      lessonPlans: data.lessonPlans || [],
      totalCount: data.totalCount || 0,
      page: data.page || 1,
      pageSize: data.pageSize || 10
    };
  }

  async getLessonPlanById(id: string): Promise<LessonPlan> {
    return apiService.get<LessonPlan>(`${this.baseUrl}/${id}`);
  }

  async getLessonPlanByIdPublic(id: string): Promise<LessonPlan> {
    // Get lesson plan metadata (public endpoint)
    const response = await fetch(`${this.baseUrl}/public/${id}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async downloadLessonPlanPublic(id: string, fileName: string): Promise<void> {
    // Download lesson plan file (public endpoint)
    const response = await fetch(`${this.baseUrl}/public/${id}/download`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('FILE_NOT_FOUND');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async getLessonPlanFilters(): Promise<{ teachers: Array<{ id: string; name: string }>; subjects: string[]; years: number[] }> {
    return apiService.get<{ teachers: Array<{ id: string; name: string }>; subjects: string[]; years: number[] }>(`${this.baseUrl}/filters`);
  }

  async getSubmissionPeriod(): Promise<{ startDate: string; endDate: string; isActive: boolean } | null> {
    try {
      return await apiService.get<{ startDate: string; endDate: string; isActive: boolean }>(`${this.baseUrl}/submission-period`);
    } catch (error) {
      logger.error('Error fetching submission period:', error);
      return null;
    }
  }

  async updateLessonPlan(id: string, formData: LessonPlanFormData): Promise<LessonPlan> {
    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    if (formData.subjectId) {
      formDataToSend.append('subjectId', formData.subjectId);
    }
    if (formData.week !== undefined) {
      formDataToSend.append('week', formData.week.toString());
    }
    if (formData.file) {
      formDataToSend.append('file', formData.file);
    }
    if (formData.folderId) {
      formDataToSend.append('folderId', formData.folderId);
    }
    if (formData.submitImmediately !== undefined) {
      formDataToSend.append('submitImmediately', formData.submitImmediately.toString());
    }

    // Digital lesson plan fields
    if ((formData as any).is_digital) {
      formDataToSend.append('is_digital', 'true');
    }
    if ((formData as any).content !== undefined) {
      formDataToSend.append('content', (formData as any).content || '');
    }

    // Add user ID to FormData as fallback (backend will check both headers and body)
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          formDataToSend.append('userId', user.id);
        }
      }
    } catch (err) {
      logger.error('❌ updateLessonPlan - Error getting userId from sessionStorage:', err);
    }

    // Get authentication headers (same as resourceService)
    const headers: HeadersInit = {};

    // Add user ID from sessionStorage (required by backend)
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    // Also add auth token as fallback (backend can use Authorization header as userId)
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add x-school-code header (required by backend)
    const schoolCode = localStorage.getItem('schoolCode');
    if (schoolCode) {
      headers['x-school-code'] = schoolCode;
    }

    // Get CSRF token from apiService
    let csrfToken = '';
    try {
      csrfToken = (apiService as any).csrfToken || '';
    } catch (_) {
      // ignore
    }

    // Add CSRF token for PUT request
    const requestHeaders: HeadersInit = {
      ...headers,
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
    };

    // Use relative path (Vite proxy will handle it)
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: requestHeaders,
      body: formDataToSend,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return await response.json();
  }

  async submitDraftLessonPlan(id: string): Promise<{ id: string; status: string; submissionDate?: string; message?: string; period?: any }> {
    return apiService.patch<{ id: string; status: string; submissionDate?: string; message?: string; period?: any }>(`${this.baseUrl}/${id}/submit`, {});
  }

  async setSubmissionPeriod(startDate: Date, endDate: Date): Promise<{ id: string; startDate: string; endDate: string; isActive: boolean }> {
    return apiService.put<{ id: string; startDate: string; endDate: string; isActive: boolean }>(`${this.baseUrl}/submission-period`, {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  }

  async getMyLessonPlans(params: LessonPlanListParams = {}): Promise<LessonPlanListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params.status) searchParams.append('status', params.status);
    searchParams.append('myPlans', 'true');

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : `${this.baseUrl}`;

    const data = await apiService.get<any>(endpoint);

    // Backend sends pagination info in response body
    return {
      lessonPlans: data.lessonPlans || [],
      totalCount: data.totalCount || 0,
      page: data.page || 1,
      pageSize: data.pageSize || 10
    };
  }

  async getLessonPlansByStatus(status: string, params: LessonPlanListParams = {}): Promise<LessonPlan[]> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page.toString());
    if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    searchParams.append('status', status);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const data = await apiService.get<any>(endpoint);
    return data.lessonPlans || [];
  }

  async submitLessonPlan(formData: LessonPlanFormData): Promise<LessonPlan> {
    const data = new FormData();

    // Handle file - only append if provided (not required for digital plans)
    if (formData.file) {
      data.append('file', formData.file);
    }

    data.append('title', formData.title);

    // Only append subjectId if it's a valid non-empty string
    if (formData.subjectId && String(formData.subjectId).trim()) {
      data.append('subjectId', String(formData.subjectId).trim());
    } else if (formData.subject && String(formData.subject).trim()) {
      // Backward compatibility
      data.append('subject', String(formData.subject).trim());
    }
    if (formData.week) {
      data.append('week', formData.week.toString());
    }
    if (formData.folderId) {
      data.append('folderId', formData.folderId);
    }
    if (formData.submitImmediately !== undefined) {
      data.append('submitImmediately', formData.submitImmediately.toString());
    }

    // Digital lesson plan fields
    if ((formData as any).is_digital) {
      data.append('is_digital', 'true');
    }
    if ((formData as any).content !== undefined) {
      data.append('content', (formData as any).content || '');
    }

    // Get user ID from sessionStorage to include in request header
    let headers: HeadersInit = {};
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    // Add tenant header (x-school-code) - required for tenant-scoped APIs
    try {
      const schoolCode = localStorage.getItem('schoolCode');
      if (schoolCode && String(schoolCode).trim()) {
        headers['x-school-code'] = String(schoolCode).trim();
      }
    } catch {
      // ignore
    }

    // Add auth token if available
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
    } catch {
      // ignore
    }

    // Use relative path (Vite proxy will handle it)
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: headers,
      body: data,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return await response.json();
  }

  async reviseLessonPlan(id: string, formData: LessonPlanRevisionFormData): Promise<LessonPlan> {
    const data = new FormData();
    data.append('file', formData.file);
    if (formData.revisionNotes) {
      data.append('revisionNotes', formData.revisionNotes);
    }

    // Get user ID from sessionStorage to include in request header
    let headers: HeadersInit = {};
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    // Add auth token if available
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
    } catch (_) {
      // ignore
    }

    // Add tenant header (x-school-code) - required for tenant-scoped APIs
    try {
      const schoolCode = localStorage.getItem('schoolCode');
      if (schoolCode && String(schoolCode).trim()) {
        headers['x-school-code'] = String(schoolCode).trim();
      }
    } catch (_) {
      // ignore
    }

    // Get CSRF token from apiService if available
    try {
      const apiService = (await import('./api')).apiService;
      // Access private csrfToken via any to get the token
      const csrfToken = (apiService as any).csrfToken;
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    } catch (_) {
      // ignore
    }

    // Use relative path (Vite proxy will handle it)
    const response = await fetch(`${this.baseUrl}/${id}/revise`, {
      method: 'POST',
      headers: headers,
      body: data,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return await response.json();
  }


  async downloadLessonPlan(id: string): Promise<Blob> {
    // Use apiService to ensure proper authentication headers and CSRF token
    const response = await fetch(`${this.baseUrl}/${id}/download`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        // Add tenant header (required)
        ...(typeof window !== 'undefined' && (() => {
          try {
            const schoolCode = localStorage.getItem('schoolCode');
            if (schoolCode && String(schoolCode).trim()) {
              return { 'x-school-code': String(schoolCode).trim() };
            }
          } catch (_) {
            // ignore
          }
          return {};
        })()),
        // Add user ID from sessionStorage (required by backend)
        ...(typeof window !== 'undefined' && (() => {
          try {
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              if (user && user.id) {
                return { 'x-user-id': user.id };
              }
            }
          } catch (_) {
            // ignore
          }
          return {};
        })()),
        // Add auth token
        ...(typeof window !== 'undefined' && (() => {
          const authToken = localStorage.getItem('authToken');
          if (authToken) {
            return { 'Authorization': `Bearer ${authToken}` };
          }
          return {};
        })()),
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  }

  async deleteLessonPlan(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/${id}`);
  }

  async bulkDeleteLessonPlans(ids: string[]): Promise<{ success: boolean; message: string; deletedCount: number; errors?: string[] }> {
    const response = await apiService.delete<{ success: boolean; message: string; deletedCount: number; errors?: string[] }>('/api/lesson-plans/bulk', {
      ids
    });
    return response;
  }

  async bulkApproveLessonPlans(ids: string[], approvalNotes?: string): Promise<{ success: boolean; message: string; approvedCount: number; totalCount: number; errors?: string[] }> {
    const response = await apiService.post<{ success: boolean; message: string; approvedCount: number; totalCount: number; errors?: string[] }>('/api/lesson-plans/bulk-approve', {
      ids,
      approvalNotes
    });
    return response;
  }

  async bulkRejectLessonPlans(ids: string[], revisionNotes: string): Promise<{ success: boolean; message: string; rejectedCount: number; totalCount: number; errors?: string[] }> {
    const response = await apiService.post<{ success: boolean; message: string; rejectedCount: number; totalCount: number; errors?: string[] }>('/api/lesson-plans/bulk-reject', {
      ids,
      revisionNotes
    });
    return response;
  }

  // Helper method to download file and trigger browser download
  async downloadLessonPlanFileDirectly(lessonPlan: LessonPlan): Promise<void> {
    try {
      const blob = await this.downloadLessonPlan(lessonPlan.id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = lessonPlan.fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error downloading lesson plan file:', error);
      throw error;
    }
  }

  // Helper method to validate file before upload
  validateFile(file: File): { isValid: boolean; error?: string } {
    return validateFileType(file);
  }

  /**
   * Check if a lesson plan file exists on the server
   * @param lessonPlan The lesson plan to check
   * @returns true if file exists or is digital, false otherwise
   */
  async checkFileExists(lessonPlan: LessonPlan): Promise<boolean> {
    // Digital plans don't need file check
    if (lessonPlan.is_digital) {
      return true;
    }

    // No file path means no file
    if (!lessonPlan.filePath) {
      return false;
    }

    try {
      // Use HEAD request to check if file exists without downloading
      const schoolCode = localStorage.getItem('schoolCode');
      const authToken = localStorage.getItem('authToken');

      const headers: HeadersInit = {};
      if (schoolCode) {
        headers['x-school-code'] = schoolCode;
      }
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${this.baseUrl}/${lessonPlan.id}/download`, {
        method: 'HEAD',
        headers,
        credentials: 'include'
      });

      return response.ok;
    } catch (error) {
      logger.error('Error checking file existence:', error);
      return false;
    }
  }

  async reviewLessonPlan(id: string, reviewNotes?: string): Promise<{ success: boolean; message: string; lessonPlan: LessonPlan }> {
    return await apiService.put<{ success: boolean; message: string; lessonPlan: LessonPlan }>(`${this.baseUrl}/${id}/review`, {
      reviewNotes: reviewNotes || undefined
    });
  }

  async approveLessonPlan(id: string, approvalNotes?: string): Promise<{ success: boolean; message: string; lessonPlan: LessonPlan }> {
    const response = await apiService.put(`${this.baseUrl}/${id}/approve`, {
      approvalNotes
    });
    return response as { success: boolean; message: string; lessonPlan: LessonPlan };
  }

  async rejectLessonPlan(id: string, revisionNotes: string): Promise<{ success: boolean; message: string; lessonPlan: LessonPlan }> {
    const response = await apiService.put(`${this.baseUrl}/${id}/reject`, {
      revisionNotes
    });
    return response as { success: boolean; message: string; lessonPlan: LessonPlan };
  }

  async exportLessonPlans(params?: {
    dateFrom?: Date | null;
    dateTo?: Date | null;
    submittedByUserId?: string;
    folderId?: string;
    subject?: string;
    year?: number;
    status?: string;
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (params?.dateFrom) {
      searchParams.append('dateFrom', params.dateFrom.toISOString().split('T')[0]);
    }
    if (params?.dateTo) {
      searchParams.append('dateTo', params.dateTo.toISOString().split('T')[0]);
    }
    if (params?.submittedByUserId) {
      searchParams.append('submittedByUserId', params.submittedByUserId);
    }
    if (params?.folderId) {
      searchParams.append('folderId', params.folderId);
    }
    if (params?.subject) {
      searchParams.append('subject', params.subject);
    }
    if (params?.year) {
      searchParams.append('year', params.year.toString());
    }
    if (params?.status) {
      searchParams.append('status', params.status);
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export?${queryString}` : `${this.baseUrl}/export`;

    const response = await apiService.get<any>(endpoint);
    // Backend /export reuses getAll() which returns { lessonPlans: [...], totalCount, ... }
    if (response && Array.isArray(response.lessonPlans)) {
      return response.lessonPlans;
    }
    return Array.isArray(response) ? response : [];
  }

  /**
   * Download lesson plan file with proper headers
   * @param id Lesson plan ID
   * @param filePath File path to download
   * @returns Blob URL for download
   */
  async downloadLessonPlanFileWithHeaders(id: string, filePath: string): Promise<string> {
    const schoolCode = localStorage.getItem('schoolCode');
    const authToken = localStorage.getItem('authToken');

    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (schoolCode) {
      headers['x-school-code'] = schoolCode;
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add user ID
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch (_) {
      // ignore
    }

    const url = `${this.baseUrl}/${id}/download?filePath=${encodeURIComponent(filePath)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}

export const lessonPlanService = new LessonPlanService();
